import { Ref, forwardRef, useCallback, useEffect, useState } from 'react';
import { getMonthNamesShort } from './consts';
import { isDateAllowed } from './utils';
import { Month } from './Month';
import { SelectMonth } from './SelectMonth';
import { SelectYear } from './SelectYear';
import { Time } from './Time';
import { YearMonthDay } from './types';
import { clamp } from 'lodash';
import { useIsMounted } from '@ardoq/hooks';
import {
  ButtonWithNav,
  DatePickerDropdownBody,
  DatePickerDropdownHeaderFooter,
  DatePickerDropdownWrapper,
} from './components';
import { SmallGhostButton } from '@ardoq/button';
import { ARDOQ_DEFAULT_LOCALE, Locale } from '@ardoq/locale';
import {
  copyDate,
  currentDate,
  MAX_YEAR,
  MIN_YEAR,
  setYearWithinRange,
} from '@ardoq/date-time';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { DateTimePickerDropdownConfig } from '../types';

export type DateTimePickerProps = {
  readonly date: Date | null;
  readonly onChange: (date: Date | null) => void;
  readonly onSelectToday?: VoidFunction;
  readonly onSelectDay?: VoidFunction;
  readonly onClear?: VoidFunction;
  readonly config?: DateTimePickerDropdownConfig;
  readonly i18nLocale?: I18nLocale;
};

type HeaderProps = {
  readonly monthName: string;
  readonly year: number;
  readonly isPreviousMonthDisabled: boolean;
  readonly isNextMonthDisabled: boolean;
  readonly isPreviousYearDisabled: boolean;
  readonly isNextYearDisabled: boolean;
  readonly toggleMonthsMode: VoidFunction;
  readonly toggleYearsMode: VoidFunction;
  readonly decrementMonth: VoidFunction;
  readonly incrementMonth: VoidFunction;
  readonly decrementYear: VoidFunction;
  readonly incrementYear: VoidFunction;
  readonly i18nLocale: I18nLocale;
};
type MainBodyProps = {
  readonly year: number;
  readonly month: number;
  readonly selected: YearMonthDay | null;
  readonly date: Date | null;
  readonly mode: Mode;
  readonly selectDay: (day: number) => void;
  readonly isDayDisabled: (date: Date) => boolean;
  readonly isMonthDisabled: (month: number) => boolean;
  readonly isYearDisabled: (year: number) => boolean;
  readonly isPreviousHourDisabled: boolean;
  readonly isPreviousMinuteDisabled: boolean;
  readonly incrementHour: VoidFunction;
  readonly decrementHour: VoidFunction;
  readonly addFifteenMinutes: VoidFunction;
  readonly subtractFifteenMinutes: VoidFunction;
  readonly selectMonth: (month: number) => void;
  readonly selectYear: (year: number) => void;
  readonly locale: Locale;
  readonly disabledDayPopoverText: string | undefined;
  readonly i18nLocale: I18nLocale;
};
type FooterProps = {
  readonly mode: Mode;
  readonly useTimePicker: boolean;
  readonly isTodayDisabled: boolean;
  readonly clearSelection: VoidFunction;
  readonly toggleTimeMode: VoidFunction;
  readonly selectToday: VoidFunction;
  readonly i18nLocale: I18nLocale;
};

enum Mode {
  DAYS = 'days',
  MONTHS = 'months',
  YEARS = 'years',
  TIME = 'time',
}

const { DAYS, MONTHS, YEARS, TIME } = Mode;

const Header = ({
  monthName,
  year,
  isPreviousMonthDisabled,
  isNextMonthDisabled,
  isPreviousYearDisabled,
  isNextYearDisabled,
  toggleMonthsMode,
  toggleYearsMode,
  decrementMonth,
  incrementMonth,
  decrementYear,
  incrementYear,
  i18nLocale,
}: HeaderProps) => {
  return (
    <DatePickerDropdownHeaderFooter>
      <ButtonWithNav
        buttonLabel={monthName}
        ariaPrevLabel={t(
          i18nLocale,
          'dateTimePicker:previousMonth',
          'Previous month'
        )}
        ariaNextLabel={t(i18nLocale, 'dateTimePicker:nextMonth', 'Next month')}
        onPrevClick={decrementMonth}
        onNextClick={incrementMonth}
        onButtonClick={toggleMonthsMode}
        buttonTooltip={t(
          i18nLocale,
          'dateTimePicker:changeMonth',
          'Change month'
        )}
        isPrevDisabled={isPreviousMonthDisabled}
        isNextDisabled={isNextMonthDisabled}
      />
      <ButtonWithNav
        buttonLabel={year.toString()}
        ariaPrevLabel={t(
          i18nLocale,
          'dateTimePicker:previousYear',
          'Previous year'
        )}
        ariaNextLabel={t(i18nLocale, 'dateTimePicker:nextYear', 'Next year')}
        onPrevClick={decrementYear}
        onNextClick={incrementYear}
        onButtonClick={toggleYearsMode}
        buttonTooltip={t(
          i18nLocale,
          'dateTimePicker:changeYear',
          'Change year'
        )}
        isPrevDisabled={isPreviousYearDisabled}
        isNextDisabled={isNextYearDisabled}
      />
    </DatePickerDropdownHeaderFooter>
  );
};

const MainBody = ({
  year,
  month,
  selected,
  date,
  mode,
  selectDay,
  isDayDisabled,
  isMonthDisabled,
  isYearDisabled,
  isPreviousHourDisabled,
  isPreviousMinuteDisabled,
  incrementHour,
  decrementHour,
  addFifteenMinutes,
  subtractFifteenMinutes,
  selectMonth,
  selectYear,
  locale,
  disabledDayPopoverText,
  i18nLocale,
}: MainBodyProps) => {
  if (mode === DAYS) {
    return (
      <Month
        {...{
          year,
          month,
          selected,
          selectDay,
          isDayDisabled,
          locale,
          disabledDayPopoverText,
          i18nLocale,
        }}
      />
    );
  }
  if (mode === MONTHS) {
    return (
      <SelectMonth
        selectedMonth={date ? date.getMonth() : null}
        isMonthDisabled={isMonthDisabled}
        selectMonth={selectMonth}
        i18nLocale={i18nLocale}
      />
    );
  }
  if (mode === YEARS) {
    return (
      <SelectYear
        year={year}
        selectedYear={date ? date.getFullYear() : null}
        isYearDisabled={isYearDisabled}
        selectYear={selectYear}
      />
    );
  }
  if (mode === TIME) {
    const dateForTime = date ?? currentDate();
    return (
      <Time
        hours={dateForTime.getHours()}
        minutes={dateForTime.getMinutes()}
        isPreviousHourDisabled={isPreviousHourDisabled}
        isPreviousMinuteDisabled={isPreviousMinuteDisabled}
        incrementHour={incrementHour}
        decrementHour={decrementHour}
        addFifteenMinutes={addFifteenMinutes}
        subtractFifteenMinutes={subtractFifteenMinutes}
      />
    );
  }
  return null;
};

const Footer = ({
  mode,
  useTimePicker,
  isTodayDisabled,
  clearSelection,
  toggleTimeMode,
  selectToday,
  i18nLocale,
}: FooterProps) => {
  return (
    <DatePickerDropdownHeaderFooter>
      {useTimePicker && (
        <SmallGhostButton onClick={toggleTimeMode}>
          {mode === TIME
            ? t(i18nLocale, 'dateTimePicker:date', 'Date')
            : t(i18nLocale, 'dateTimePicker:time', 'Time')}
        </SmallGhostButton>
      )}
      <SmallGhostButton onClick={selectToday} isDisabled={isTodayDisabled}>
        {t(i18nLocale, 'dateTimePicker:today', 'Today')}
      </SmallGhostButton>
      <SmallGhostButton onClick={clearSelection}>
        {t(i18nLocale, 'dateTimePicker:clear', 'Clear')}
      </SmallGhostButton>
    </DatePickerDropdownHeaderFooter>
  );
};

const DateTimePicker = (
  {
    date,
    onChange,
    onSelectToday,
    onSelectDay,
    onClear,
    config = {},
    i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  }: DateTimePickerProps,
  ref: Ref<HTMLDivElement>
) => {
  const referenceDate = date ?? currentDate();
  const [displayMonth, setDisplayMonth] = useState(referenceDate.getMonth());
  const [displayYear, setDisplayYear] = useState(referenceDate.getFullYear());
  const [selectedDate, setSelectedDate] = useState(referenceDate.getDate());
  const [mode, setMode] = useState(DAYS);
  const clearSelection = useCallback(() => {
    const newDate = currentDate();
    setDisplayMonth(newDate.getMonth());
    setDisplayYear(newDate.getFullYear());
    onChange(null);
  }, [onChange, setDisplayMonth, setDisplayYear]);
  const selectToday = useCallback(() => {
    const newDate = currentDate();
    setDisplayMonth(newDate.getMonth());
    setDisplayYear(newDate.getFullYear());
    onChange(newDate);
  }, [onChange, setDisplayMonth, setDisplayYear]);
  const toggleMonthsMode = useCallback(() => {
    setMode(mode === MONTHS ? DAYS : MONTHS);
  }, [mode, setMode]);
  const toggleYearsMode = useCallback(() => {
    setMode(mode === YEARS ? DAYS : YEARS);
  }, [mode, setMode]);
  const toggleTimeMode = useCallback(() => {
    setMode(mode === TIME ? DAYS : TIME);
  }, [mode, setMode]);
  const decrementHour = useCallback(() => {
    const delta = -1;
    const newDate = getDateModifiedByHourDelta(
      date,
      delta,
      displayYear,
      displayMonth
    );
    onChange(newDate);
  }, [date, displayYear, displayMonth, onChange]);
  const incrementHour = useCallback(() => {
    const delta = 1;
    const newDate = getDateModifiedByHourDelta(
      date,
      delta,
      displayYear,
      displayMonth
    );
    onChange(newDate);
  }, [date, displayYear, displayMonth, onChange]);
  const addFifteenMinutes = useCallback(() => {
    const delta = 15;
    const newDate = getDateModifiedByMinuteDelta(
      date,
      delta,
      displayYear,
      displayMonth
    );
    onChange(newDate);
  }, [date, displayYear, displayMonth, onChange]);
  const subtractFifteenMinutes = useCallback(() => {
    const delta = -15;
    const newDate = getDateModifiedByMinuteDelta(
      date,
      delta,
      displayYear,
      displayMonth
    );
    onChange(newDate);
  }, [date, displayYear, displayMonth, onChange]);
  const decrementMonth = useCallback(() => {
    const delta = -1;
    const newDate = date ? copyDate(date) : currentDate();
    newDate.setFullYear(displayYear);
    setMonth(newDate, displayMonth + delta, selectedDate);
    setDisplayMonth(newDate.getMonth());
    setDisplayYear(clamp(newDate.getFullYear(), MIN_YEAR, MAX_YEAR));
  }, [
    date,
    displayYear,
    displayMonth,
    selectedDate,
    setDisplayMonth,
    setDisplayYear,
  ]);
  const incrementMonth = useCallback(() => {
    const delta = 1;
    const newDate = date ? copyDate(date) : currentDate();
    newDate.setFullYear(displayYear);
    setMonth(newDate, displayMonth + delta, selectedDate);
    setDisplayMonth(newDate.getMonth());
    setDisplayYear(clamp(newDate.getFullYear(), MIN_YEAR, MAX_YEAR));
  }, [
    date,
    displayYear,
    displayMonth,
    selectedDate,
    setDisplayMonth,
    setDisplayYear,
  ]);
  const decrementYear = useCallback(() => {
    setDisplayYear(clamp(displayYear - 1, MIN_YEAR, MAX_YEAR));
  }, [displayYear, setDisplayYear]);
  const incrementYear = useCallback(() => {
    setDisplayYear(clamp(displayYear + 1, MIN_YEAR, MAX_YEAR));
  }, [displayYear, setDisplayYear]);
  const selectDay = useCallback(
    (dayValue: number) => {
      if (isNaN(dayValue) || dayValue <= 0) {
        return;
      }
      const newDate = date ? copyDate(date) : currentDate();
      newDate.setFullYear(displayYear);
      newDate.setMonth(displayMonth, dayValue);
      setSelectedDate(newDate.getDate());
      onChange(newDate);
    },
    [date, onChange, setSelectedDate, displayYear, displayMonth]
  );
  const selectMonth = useCallback(
    (monthValue: number) => {
      if (isNaN(monthValue) || monthValue < 0) {
        return;
      }
      const newDate = getDateModifiedByMonthSelection(
        date,
        monthValue,
        displayYear,
        selectedDate
      );
      setMode(DAYS);
      setDisplayMonth(newDate.getMonth());
      onChange(newDate);
    },
    [date, onChange, displayYear, selectedDate, setMode, setDisplayMonth]
  );

  const [isVisible, setIsVisible] = useState<boolean>(false);
  const isMounted = useIsMounted();
  useEffect(() => {
    if (isMounted) {
      setTimeout(() => setIsVisible(isMounted));
    }
  }, [isMounted]);

  const selectYear = useCallback(
    (yearValue: number) => {
      if (isNaN(yearValue) || yearValue <= 0) {
        return;
      }
      const newDate = getDateModifiedByYearSelection(
        date,
        yearValue,
        displayMonth,
        selectedDate
      );
      setMode(DAYS);
      setDisplayYear(newDate.getFullYear());
      onChange(newDate);
    },
    [date, onChange, displayMonth, selectedDate, setMode, setDisplayYear]
  );

  const selected = date
    ? {
        year: date.getFullYear(),
        month: date.getMonth(),
        day: date.getDate(),
      }
    : null;
  const containerStyle = config?.containerStyle ?? {};
  const { locale = ARDOQ_DEFAULT_LOCALE } = config;

  return (
    <DatePickerDropdownWrapper
      role="dialog"
      ref={ref}
      style={containerStyle}
      $isVisible={isVisible}
      data-testid="date-time-picker-dropdown"
    >
      <Header
        monthName={getMonthNamesShort(i18nLocale)[displayMonth]}
        year={setYearWithinRange(displayYear)}
        isPreviousMonthDisabled={mode !== DAYS}
        isNextMonthDisabled={mode !== DAYS}
        isPreviousYearDisabled={
          mode !== DAYS || setYearWithinRange(displayYear) === MIN_YEAR
        }
        isNextYearDisabled={
          mode !== DAYS || setYearWithinRange(displayYear) === MAX_YEAR
        }
        toggleMonthsMode={toggleMonthsMode}
        toggleYearsMode={toggleYearsMode}
        decrementMonth={decrementMonth}
        incrementMonth={incrementMonth}
        decrementYear={decrementYear}
        incrementYear={incrementYear}
        i18nLocale={i18nLocale}
      />
      <DatePickerDropdownBody data-test-id={'date-time-picker-dropdown-body'}>
        <MainBody
          year={setYearWithinRange(displayYear)}
          month={displayMonth}
          selected={selected}
          date={date}
          mode={mode}
          locale={locale}
          selectDay={day => {
            selectDay(day);
            onSelectDay?.();
          }}
          isDayDisabled={(d: Date) => !isDateAllowed(d, config)}
          isMonthDisabled={(potentialMonthIndex: number) =>
            !isDateAllowed(
              getDateModifiedByMonthSelection(
                date,
                potentialMonthIndex,
                displayYear,
                selectedDate
              ),
              config
            )
          }
          isYearDisabled={(potentialYear: number) =>
            !isDateAllowed(
              getDateModifiedByYearSelection(
                date,
                potentialYear,
                displayMonth,
                selectedDate
              ),
              config
            )
          }
          isPreviousHourDisabled={
            !isDateAllowed(
              getDateModifiedByHourDelta(date, -1, displayYear, displayMonth),
              config
            )
          }
          isPreviousMinuteDisabled={
            !isDateAllowed(
              getDateModifiedByMinuteDelta(
                date,
                -15,
                displayYear,
                displayMonth
              ),
              config
            )
          }
          incrementHour={incrementHour}
          decrementHour={decrementHour}
          addFifteenMinutes={addFifteenMinutes}
          subtractFifteenMinutes={subtractFifteenMinutes}
          selectMonth={selectMonth}
          selectYear={selectYear}
          disabledDayPopoverText={config?.disabledDayExplanationText}
          i18nLocale={i18nLocale}
        />
      </DatePickerDropdownBody>
      <Footer
        mode={mode}
        useTimePicker={!!config?.useTimePicker}
        isTodayDisabled={!isDateAllowed(currentDate(), config)}
        clearSelection={() => {
          clearSelection();
          onClear?.();
        }}
        toggleTimeMode={toggleTimeMode}
        selectToday={() => {
          selectToday();
          onSelectToday?.();
        }}
        i18nLocale={i18nLocale}
      />
    </DatePickerDropdownWrapper>
  );
};

function getDateModifiedByMonthSelection(
  date: Date | null,
  monthIndex: number,
  displayYear: number,
  selectedDate: number
) {
  const newDate = date ? copyDate(date) : currentDate();
  newDate.setFullYear(displayYear);
  setMonth(newDate, monthIndex, selectedDate);
  return newDate;
}

function getDateModifiedByYearSelection(
  date: Date | null,
  year: number,
  displayMonth: number,
  selectedDate: number
) {
  const newDate = date ? copyDate(date) : currentDate();
  newDate.setMonth(displayMonth);
  setYear(newDate, year, selectedDate);
  return newDate;
}

function getDateModifiedByMinuteDelta(
  date: Date | null,
  delta: number,
  displayYear: number,
  displayMonth: number
) {
  const newDate = date ? copyDate(date) : currentDate();
  let minutes = newDate.getMinutes();
  if (minutes % 15 !== 0) {
    minutes = Math.round(minutes / 15) * 15;
  }
  newDate.setFullYear(displayYear);
  newDate.setMonth(displayMonth);
  newDate.setMinutes(minutes + delta);
  return newDate;
}

function getDateModifiedByHourDelta(
  date: Date | null,
  delta: number,
  displayYear: number,
  displayMonth: number
) {
  const newDate = date ? copyDate(date) : currentDate();
  newDate.setFullYear(displayYear);
  newDate.setMonth(displayMonth);
  newDate.setHours(newDate.getHours() + delta);
  return newDate;
}

function setYear(date: Date, year: number, selectedDate: number) {
  const month = date.getMonth();
  date.setFullYear(year);
  retainMonthAndSelectedDate(date, month, selectedDate);
}

function setMonth(date: Date, month: number, selectedDate: number) {
  date.setMonth(month);
  retainMonthAndSelectedDate(date, month, selectedDate);
}

function retainMonthAndSelectedDate(
  date: Date,
  month: number,
  selectedDate: number
) {
  let currentMonth = month;
  while (currentMonth < 0) {
    currentMonth = (currentMonth + 12) % 12;
  }
  if (date.getMonth() > currentMonth) {
    while (date.getMonth() > currentMonth) {
      date.setDate(date.getDate() - 1);
    }
  } else if (date.getDate() < selectedDate) {
    const testDate = copyDate(date);
    while (date.getDate() < selectedDate) {
      testDate.setDate(date.getDate() + 1);
      if (date.getMonth() === testDate.getMonth()) {
        date.setDate(date.getDate() + 1);
      } else {
        break;
      }
    }
  }
}

export const DateTimePickerDropdown = forwardRef(DateTimePicker);
